import create from 'zustand'
import { getBillingEntitlements } from '../api/billingApi'
import { Logger } from '../logger'
import { useUserStore } from './UserStore'
import { entitlementUserKey } from '../data/entitlementUser'

// The last answer, kept so a page refresh paints the right sidebar straight away instead of
// waiting for the network. It is only a starting point: it is shown only to the same user at
// the same center, and is always re-fetched on load (the backend enforces regardless).
const CACHE_KEY = 'entitlements-snapshot'

const initialState = {
  // From GET /v1/billing/entitlements for the active center. `modules` is what THIS user may use:
  // the package's features narrowed by their role (backend RoleModulePolicy). `planModules` is the
  // package alone, so a refusal can say "upgrade" (not in the plan) or "not for your role".
  modules: [],
  planModules: [],
  // The backend refused to say (4xx): treat as "nothing gated is available", not "everything".
  loadFailed: false,
  // The backend couldn't be reached and nothing was known before: show everything (an outage
  // must not lock the app); the backend still refuses what the plan lacks.
  failOpen: false,
  // On a free trial while trials are frozen (backend billing.trials.frozen): locked until it pays.
  trialPaused: false,
  tier: null,
  state: null,
  hasSubscription: null,
  managedExternally: false,
  writeBlocked: false,
  // When the trial ends (ISO string, TRIAL only) — the header badge counts down from it.
  trialEndDate: null,
  // Whether the plan includes body-shop sign-in (0173). A client user without it gets no modules.
  clientPortal: true,
  // The plan's limits and this location's usage (0173): [{ key, label, limit, display, used, remaining, upgradeTier, … }].
  limits: [],
  // Repair orders currently locked by the monthly RO limit.
  lockedRos: 0,
  // Which center and user the data belongs to; the gating helpers treat anything else as "unknown".
  loadedForCcId: null,
  loadedForUser: null,
  // True once the data came from the backend in this page load (false for the cached snapshot).
  fresh: false,
  isLoading: false
}

const fromView = (view) => ({
  modules: view?.modules || [],
  planModules: view?.planModules || view?.modules || [],
  tier: view?.tier ?? null,
  state: view?.state ?? null,
  hasSubscription: view?.hasSubscription ?? null,
  managedExternally: Boolean(view?.managedExternally),
  writeBlocked: Boolean(view?.writeBlocked),
  trialPaused: Boolean(view?.trialPaused),
  trialEndDate: view?.trialEndDate ?? null,
  clientPortal: view?.clientPortal !== false,
  limits: Array.isArray(view?.limits) ? view.limits : [],
  lockedRos: Number(view?.lockedRos) || 0
})

const readSnapshot = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(CACHE_KEY))
    if (!saved?.ccId || !saved?.user) return {}
    return { ...fromView(saved.view), loadedForCcId: saved.ccId, loadedForUser: saved.user }
  } catch {
    return {}
  }
}

const writeSnapshot = (snapshot) => {
  try {
    if (snapshot) localStorage.setItem(CACHE_KEY, JSON.stringify(snapshot))
    else localStorage.removeItem(CACHE_KEY)
  } catch {
    /* storage unavailable: the sidebar just waits for the network */
  }
}

/**
 * The active center's feature entitlements (CAR-3710). Loaded by PageLayout whenever the
 * center changes and refreshed when the backend refuses a call for a billing reason, so the
 * nav, routes and status bar track the subscription without a reload. Starts from the last
 * snapshot for the same user and center, then re-fetches.
 */
const useEntitlementStore = create((set, get) => ({
  ...initialState,
  ...readSnapshot(),

  reset: () => {
    writeSnapshot(null)
    set({ ...initialState })
  },

  loadEntitlements: async (ccId, { force = false } = {}) => {
    if (!ccId) {
      set({ ...initialState })
      return
    }
    const user = entitlementUserKey(useUserStore.getState().currentUser)
    const current = get()
    const sameOwner = current.loadedForCcId === ccId && current.loadedForUser === user
    if (!force && ((sameOwner && current.fresh) || current.isLoading)) return
    // Someone else's (or another center's) answer must not show while this one loads.
    set(sameOwner ? { isLoading: true } : { ...initialState, isLoading: true })
    try {
      const view = await getBillingEntitlements(ccId)
      set({ ...initialState, ...fromView(view), loadedForCcId: ccId, loadedForUser: user, fresh: true })
      writeSnapshot(user ? { ccId, user, view } : null)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'EntitlementStore', method: 'loadEntitlements', ccId } })
      const status = error?.response?.status
      if (status >= 400 && status < 500) {
        // A refusal is an answer: show no gated section rather than every section. (This is
        // how a body-shop user once saw Work Orders on an AI Report plan.)
        writeSnapshot(null)
        set({ ...initialState, loadedForCcId: ccId, loadedForUser: user, loadFailed: true, fresh: true })
        return
      }
      // Network / server trouble: keep the last known answer for this user and center if there
      // is one, otherwise fail open so an outage doesn't lock the app. Retried on the next load.
      set(sameOwner ? { isLoading: false } : { ...initialState, loadedForCcId: ccId, loadedForUser: user, failOpen: true })
    }
  }
}))

export { useEntitlementStore }
export default useEntitlementStore
